import type { DealSummary } from "@/hooks/use-deals";
import { DealMomentumBadge } from "@/components/crm/deal-momentum-badge";

function getTemperature(lastHumanActivityAt: string | null) {
  if (!lastHumanActivityAt) {
    return null;
  }

  const activityTime = new Date(lastHumanActivityAt).getTime();
  if (Number.isNaN(activityTime)) {
    return null;
  }

  const daysSinceActivity = (Date.now() - activityTime) / 86_400_000;
  if (daysSinceActivity < 2) {
    return {
      label: "Hot",
      dotTone: "bg-[var(--color-success)]",
      textTone: "text-[var(--color-success)]",
    };
  }
  if (daysSinceActivity < 7) {
    return {
      label: "Warm",
      dotTone: "bg-[var(--color-warning)]",
      textTone: "text-[var(--color-warning)]",
    };
  }
  return {
    label: "Cold",
    dotTone: "bg-[var(--color-muted-foreground)]",
    textTone: "text-[var(--color-muted-foreground)]",
  };
}

function getNextAction(customFields: Record<string, unknown> | undefined) {
  if (!customFields) {
    return null;
  }

  const candidateKeys = ["nextAction", "next_action", "aiNextAction", "recommendedAction", "recommended_action"];

  for (const key of candidateKeys) {
    const value = customFields[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

export function DealAiIndicators({ deal }: { deal: DealSummary }) {
  const temperature = getTemperature(deal.lastHumanActivityAt);
  const nextAction = getNextAction(deal.customFields);

  if (!temperature && !deal.momentumDirection && !nextAction && deal.winProbability == null) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2 border-t border-border/70 pt-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {temperature ? (
          <span className={`inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2 py-0.5 font-medium ${temperature.textTone}`}>
            <span className={`h-2 w-2 rounded-full ${temperature.dotTone}`} aria-hidden />
            {temperature.label}
          </span>
        ) : null}
        {deal.momentumDirection ? (
          <DealMomentumBadge
            direction={deal.momentumDirection}
            score={deal.momentumScore ?? undefined}
            compact
          />
        ) : null}
        {deal.winProbability != null ? (
          <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 font-medium text-[var(--color-primary)]">
            Win {Math.round(deal.winProbability)}%
          </span>
        ) : null}
      </div>
      {nextAction ? (
        <p className="line-clamp-2 text-xs text-foreground/80">
          AI next action: <span className="font-medium text-foreground">{nextAction}</span>
        </p>
      ) : null}
    </div>
  );
}
